import { useMemo } from 'react';
import { Collection } from '../../../../services/entities/impl/blfApi/schemas/collection';
import { useTranslation } from '../../../../services/translation/hooks/useTranslation';
/**
 * A hook that generates a formatted label string for a collection.
 *
 * @param collection - The collection object.
 *
 * @returns A formatted string combining the collection's camera ID, collection date,
 * and SSD ID separated by slashes. If any value is missing, a localized
 * placeholder is used instead.
 *
 * @example
 * // Returns "2023-01-01 / Camera123 / SSD456"
 * const label = useCreateCollectionLabel(
 *   collection: {
 *     collectionDate: "2023-01-01",
 *     cameraID: "Camera123",
 *     ssdID: "SSD456"
 *   }
 * );
 */
export const useCreateCollectionLabel = (
  collection: Collection | undefined
) => {
  const { t } = useTranslation();
  const label = useMemo(() => {
    if (!collection) {
      return '';
    }

    const collectionDate = collection.collectionDate || t('No Date');
    const cameraID = collection.cameraID || t('No Camera ID');
    const ssdID = collection.ssdID || t('No SSD ID');

    return `${collectionDate} / ${cameraID} / ${ssdID}`;
  }, [t, collection]);

  return label;
};
